import React from 'react';
import LazyLoad from 'react-lazyload';
import ReactPlayer from 'react-player';
import { PlayButton } from '@tmap/mmm-style-guide/src/Icon';
import { Card } from '@tmap/mmm-style-guide/src/Card';
import { styled } from '@tmap/mmm-style-guide/src/styled';
import { Chip } from '@tmap/mmm-style-guide/src/Chip';
import { Box } from '@tmap/mmm-style-guide/src/Box';
import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';
import ResponsivePicture from '../responsivePicture';

const getPalette = (theme, palette) => (({
  primary: {
    borderColor: theme.palette.primary.dark,
    backgroundColor: theme.palette.primary.light,
    chipColor: theme.palette.green.light,
    chipTextColor: theme.palette.text.primary,
    textColor: theme.palette.secondary.veryLight,
    frameLight: theme.palette.common.white,
    frameDark: theme.palette.primary.veryDark,
  },
  purple: {
    borderColor: theme.palette.purple.main,
    backgroundColor: theme.palette.purple.main,
    chipColor: theme.palette.common.white,
    chipTextColor: theme.palette.text.primary,
    textColor: theme.palette.purple.veryLight,
    frameLight: theme.palette.common.white,
    frameDark: theme.palette.purple.veryDark,
  },
  primaryLight: {
    borderColor: '#d7e3e5',
    backgroundColor: theme.palette.primary.veryLight,
    chipColor: theme.palette.secondary.main,
    chipTextColor: theme.palette.common.white,
    textColor: theme.palette.primary.dark,
    frameLight: theme.palette.common.white,
    frameDark: theme.palette.secondary.veryDark,
  },
})[palette]);

const BannerCard = styled(Card, {
  shouldForwardProp: (p) => !['colorPalette'].includes(p),
})(({ theme, colorPalette }) => {
  const palette = getPalette(theme, colorPalette);
  return ({
    background: palette.backgroundColor,
    color: palette.textColor,
    border: `3px solid ${palette.borderColor}`,
    borderRadius: '16px',
    display: 'inline-block',
    maxWidth: '100%',
    width: '100%',
    padding: theme.spacing(3.5, 7, 3.5, 3.5),
    [theme.breakpoints.down('md')]: {
      width: 'unset',
      padding: theme.spacing(3.5, 2.5),
    },
  });
});

const BannerChip = styled(Chip, {
  shouldForwardProp: (p) => !['colorPalette'].includes(p),
})(({ theme, colorPalette }) => {
  const palette = getPalette(theme, colorPalette);
  return ({
    background: palette.chipColor,
    color: palette.chipTextColor,
    marginTop: theme.spacing(1.25),
    '& .MuiChip-label': {
      padding: theme.spacing(0, 2),
    },
  });
});

const AssetFrame = styled(Box, {
  shouldForwardProp: (p) => !['colorPalette', 'assetColor'].includes(p),
})(({ theme, colorPalette, assetColor }) => {
  const palette = getPalette(theme, colorPalette);
  const borderColor = ({
    light: palette.frameLight,
    dark: palette.frameDark,
  })[assetColor];
  return ({
    border: `16px solid ${borderColor}`,
    background: borderColor,
    borderRadius: '16px',
    maxWidth: '100%',
    overflow: 'hidden',
  });
});

const CopyWrapper = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2.25),
  '& .MuiTypography-root': {
    fontSize: '22px',
    lineHeight: '32px',
    [theme.breakpoints.down('md')]: {
      fontSize: '20px',
    },
  },
}));

const BannerGrid = styled(Grid)(({ theme }) => ({
  minHeight: '200px',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  alignItems: 'center',
  [theme.breakpoints.down('md')]: {
    display: 'inline-flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
}));

function BannerAsset(props) {
  const { bannerImage, bannerVideoUrl } = props;
  const picture = (
    <ResponsivePicture
      image={bannerImage}
      width={300}
      xs={2}
      style={{
        objectFit: 'cover',
        borderRadius: '16px',
        maxWidth: '100%',
        overflow: 'hidden',
      }}
    />
  );
  if (bannerVideoUrl) {
    return (
      <Box width={300} maxWidth='100%'>
        <Box
          sx={{
            position: 'relative',
            paddingTop: '56.25%',
          }}
        >
          <ReactPlayer
            src={bannerVideoUrl}
            controls
            light={bannerImage ? picture : true}
            playing
            width='100%'
            height='100%'
            playIcon={<PlayButton sx={{ position: 'absolute', fontSize: '64px' }} />}
            wrapper='div'
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              borderRadius: '16px',
              overflow: 'hidden',
            }}
          />
        </Box>
      </Box>
    );
  }
  return (
    <LazyLoad offset={400}>
      {picture}
    </LazyLoad>
  );
}

function Banner(props) {
  const {
    RecursiveComponent,
    overlineCopy,
    leadCopy,
    colorPalette,
    assetColor,
    bannerImage,
    bannerVideoUrl,
  } = props;
  return (
    <BannerCard elevation={0} colorPalette={colorPalette}>
      <BannerGrid container rowSpacing={4} columnSpacing={7.5}>
        {(bannerImage || bannerVideoUrl) && (
          <Grid item sx={{ maxWidth: { xs: '100%', md: '50%' } }}>
            <AssetFrame colorPalette={colorPalette} assetColor={assetColor}>
              <BannerAsset bannerImage={bannerImage} bannerVideoUrl={bannerVideoUrl} />
            </AssetFrame>
          </Grid>
        )}
        <Grid item xs sx={{ width: { xs: '392px', md: 'auto' }, minWidth: '330px' }}>
          {overlineCopy && (
            <BannerChip
              label={
                <Typography variant='overline' letterSpacing='1px'>{overlineCopy}</Typography>
              }
              colorPalette={colorPalette}
            />
          )}
          <CopyWrapper>
            <RecursiveComponent value={leadCopy} nested />
          </CopyWrapper>
        </Grid>
      </BannerGrid>
    </BannerCard>
  );
}

export default Banner;
